import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import { leftpanel } from '../config/leftpanel.config';
import { audit } from '../config/audit.config';
import { IReportChart } from '../types/report';
import { IPerformAuditCriteriaData } from '../types/auditdata';

import MapController from '../controllers/MapController';

class HansenChartController {
  _hansenReportCharts: Array<IReportChart> = [];
  createHansenChart = (criteriaNumber: string, hansenData: Array<number>) => {
    const { categories, seriesData } = this.generateCatergoriesAndData(hansenData, false);
    Exporting(Highcharts);

    Highcharts.chart(`criterion-${criteriaNumber}-chart`, {
      chart: {
        type: 'column',
        width: 300,
        height: 250,
      },
      credits: {
        enabled: false,
      },
      colors: ['#C4E1D9'],
      xAxis: {
        categories: categories,
        labels: {
          formatter: function (this: any) {
            return `${this.value.toLocaleString()}`;
          },
        },
      },
      yAxis: {
        title: {
          text: 'Hectares',
        },
        labels: {
          formatter: function (this: any) {
            return `${this.value.toLocaleString()}`;
          },
        },
      },
      title: {
        text: 'Hectares Lost',
      },
      subtitle: {
        text: criteriaNumber, //Store criteria number but don't display it - need it for updating
        style: { display: 'none' },
      },
      tooltip: {
        formatter: function (this: any) {
          const hansenLayer = MapController._map?.findLayerById('hansen');
          const startingDate = (hansenLayer as __esri.ImageryLayer).renderingRule.functionArguments.hasOwnProperty(
            'Raster'
          )
            ? (hansenLayer as __esri.ImageryLayer).renderingRule.functionArguments.Raster.rasterFunctionArguments
                .IncludedRanges[0]
            : (hansenLayer as __esri.ImageryLayer).renderingRule.functionArguments.IncludedRanges[0];
          const date = Number(this.point.x + startingDate + 2000);
          return `<b>Year ${date}</b>: ${Number(this.point.y).toLocaleString()}`;
        },
      },
      series: [{ showInLegend: false, data: seriesData, type: 'column' }],
      exporting: {
        buttons: {
          contextButton: {
            menuItems: ['downloadPNG'], // only want the Download PNG option
          },
        },
      },
    });
  };

  prepareChartsToPrint = async (performAuditLayers: Array<IPerformAuditCriteriaData>) => {
    this._hansenReportCharts = [];
    const charts = Highcharts.charts.filter((chart) => chart);
    if (charts && performAuditLayers.length > 0) {
      for (let chart of charts) {
        if (chart) {
          let pal = performAuditLayers.find((pal) => chart?.subtitle.element.textContent === pal.criteriaNumber);
          if (pal) {
            await this.generateReportChartObject(pal.criteriaNumber, pal.hansenCounts, pal.selectedLayer.title, true);
          }
        }
      }
    }
    return this._hansenReportCharts;
  };

  updateHansenChart = (performAuditLayers: Array<IPerformAuditCriteriaData>, values: Array<number>) => {
    const charts = Highcharts.charts.filter((chart) => chart);
    if (charts && performAuditLayers.length > 0) {
      charts.forEach((chart) => {
        if (chart) {
          let pal = performAuditLayers.find((pal) => chart?.subtitle.element.textContent === pal.criteriaNumber);
          if (pal) {
            const { categories, seriesData } = this.generateCatergoriesAndData(pal.hansenCounts, false, values);
            chart?.update({
              xAxis: {
                categories,
              },
              series: [{ showInLegend: false, data: seriesData, type: 'column' }],
            });
          }
        }
      });
    }
  };

  generateCatergoriesAndData = (hansenData: Array<number>, reportChart: boolean, values?: Array<number>) => {
    let categories: Array<string>, seriesData: Array<number>;
    if (hansenData && hansenData.length > 0) {
      hansenData = hansenData.slice(1); // we slice out the first index because it returns a total that is not relevant to the chart
      categories = hansenData.map((point, i) => (i + 2001).toString());
      seriesData = hansenData.map((point) => point * 0.09); // each hansen pixel is 0.09 hectares, so multiply hansen values by 0.09
    } else {
      // If no hansenData, we want to create an empty chart retaining the labels
      categories = Array(19).fill(0).map((point, i) => (i + 2001).toString());
      seriesData = categories.map(i => 0);
    }

    // If we're generating the chart for the report, we want all values
    if (reportChart) {
      return {
        categories,
        seriesData,
      };
    }

    let startingPoint, endPoint;
    const hansenLayer = MapController._map?.findLayerById('hansen');
    if (!values) {
      // If we aren't provided values specifically, the most up-to-date values for the Tree Loss slider is on the Hansen Layer itself
      if (hansenLayer) {
        const includedRanges = (hansenLayer as __esri.ImageryLayer).renderingRule.functionArguments.hasOwnProperty(
          'Raster'
        )
          ? (hansenLayer as __esri.ImageryLayer).renderingRule.functionArguments.Raster.rasterFunctionArguments
              .IncludedRanges
          : (hansenLayer as __esri.ImageryLayer).renderingRule.functionArguments.IncludedRanges;
        startingPoint = includedRanges[0];
        endPoint = includedRanges[1];
      }
    } else {
      startingPoint = values[0];
      endPoint = values[1];
    }

    if (
      categories.length === 0 ||
      (values
        ? !categories.includes(Number(values[1] + 2000).toString())
        : Number(categories[categories.length - 1]) !== leftpanel.hansenEndYear)
    ) {
      // this conditional is in place to fill missing values -- hansen will return incomplete arrays if multiple 0s exist at the end of the array
      for (let i = 0; i <= Number(leftpanel.hansenEndYear - 2000); i++) {
        if (!categories[i]) {
          categories[i] = Number(i + 2001).toString();
          seriesData[i] = 0;
        }
      }
    }

    // restrict data by start/end
    categories = categories.slice(startingPoint - 1, endPoint);
    seriesData = seriesData.slice(startingPoint - 1, endPoint);

    return {
      categories,
      seriesData,
    };
  };

  generateReportChartObject = (criteriaNumber: string, hansenData: Array<number>, layerTitle: string, reportChart: boolean = false) => {
    let { categories, seriesData } = this.generateCatergoriesAndData(hansenData, reportChart);
    if (!categories || categories.length === 0) {
      categories = Array(19).fill(0).map((point, i) => (i + 2001).toString());
    }
    // We have to create a duplicate of the chart at the size we want to print it at
    const chartToPrint = Highcharts.chart(`criterion-${criteriaNumber}-chart-hidden`, {
      chart: {
        type: 'column',
        width: 1400,
        height: 1000,
        style: {
          fontSize: '46px',
        },
      },
      colors: ['#C4E1D9'],
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: categories,
        labels: {
          formatter: function (this: any) {
            return `${this.value.toLocaleString()}`;
          },
          style: {
            fontSize: '35px',
          },
        },
      },
      yAxis: {
        title: {
          text: 'Hectares',
        },
        labels: {
          formatter: function (this: any) {
            return `${this.value.toLocaleString()}`;
          },
          style: {
            fontSize: '35px',
          },
        },
      },
      title: {
        text: 'Hectares Lost',
        style: {
          fontSize: '46px',
        },
      },
      tooltip: {
        formatter: function (this: any) {
          const date = Number(this.point.x + 2001);
          return `<b>Year ${date}</b>: ${Number(this.point.y).toLocaleString()}`;
        },
      },
      series: [{ showInLegend: false, data: seriesData, type: 'column' }],
      exporting: {
        buttons: {
          contextButton: {
            menuItems: ['downloadPNG'], // only want the Download PNG option
          },
        },
      },
    });

    const principleNumber = Number(criteriaNumber.split('.')[0]);
    const principle = audit.principles.find((item) => item.principle === principleNumber)?.title;
    const svg = chartToPrint.getSVG();

    const canvas = document.createElement('canvas');
    canvas.width = chartToPrint.chartWidth;
    canvas.height = chartToPrint.chartHeight;
    const ctx = canvas.getContext('2d');

    const img = document.createElement('img');

    const treeLossIndex = categories.indexOf(String(leftpanel.hansenEndYear));
    let treeLoss: number;
    if (treeLossIndex) {
      treeLoss = seriesData[treeLossIndex];
    }

    return new Promise((resolve, reject) => {
      img.onload = () => {
        ctx?.drawImage(img, 0, 0);
        this._hansenReportCharts.push({
          chart: canvas.toDataURL('image/png'),
          title: `Principle ${criteriaNumber}: ${principle} - ${layerTitle}`,
          criteriaNumber,
          treeLoss,
        });
        //Destroy once we're done
        chartToPrint.destroy();
        resolve(null);
      };
      img.setAttribute('src', 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg))));
    });
  };

  destroyChart = (criteriaNumber: string) => {
    const chart = Highcharts.charts.find((chart) => chart?.subtitle.element.textContent === criteriaNumber);
    if (chart) {
      chart.destroy();
      this._hansenReportCharts = this._hansenReportCharts.filter((chart) => chart.criteriaNumber !== criteriaNumber);
    }
  };
}

const hansenChartController = new HansenChartController();

export default hansenChartController;
